<template>
  <v-card flat class="image-card">
    <v-hover v-if="imageUrl" v-slot:default="{ hover }">
      <v-img
        class="image-box"
        :src="imageUrl"
        contain
        @error="repairBrokenImage"
      >
        <div v-if="repairing" class="image-repairing">
          <v-progress-circular indeterminate size="28" color="primary" />
          <span>Repairing image…</span>
        </div>
        <v-layout v-if="hover" column fill-height justify-center align-center>
          <v-flex shrink>
            <v-btn @click="imageRemove" color="error" small>Remove</v-btn>
          </v-flex>
          <v-flex class="mt-1" shrink>
            <v-btn @click="openMore" color="purple" dark small>
              <span v-if="imageResult">More Image</span>
              <span v-else>Add Image</span>
            </v-btn>
          </v-flex>
        </v-layout>
      </v-img>
    </v-hover>

    <v-hover v-else v-slot:default="{ hover }">
      <v-img
        class="image-box"
        src="@/assets/images/NoPhotoAvailable.jpg"
        contain
      >
        <v-layout v-if="hover" column fill-height justify-center align-center>
          <v-flex shrink>
            <v-btn @click="openMore" color="purple" dark small>
              <span v-if="imageResult">More Image</span>
              <span v-else>Add Image</span>
            </v-btn>
          </v-flex>
        </v-layout>
      </v-img>
    </v-hover>

    <MoreImage
      :name="name"
      :brand="brand"
      :upc="upc"
      :sku="sku"
      :field="field"
      :showDialog="showDialog"
      :imageResult="imageResult"
      :autoSearch="autoRepair"
      @close="showDialog = false"
      @submit="imageChange"
      @uploaded="imageUploaded"
    />
  </v-card>
</template>

<script>
import MoreImage from "@/components/Image/MoreImage";
import productService from "@/modules/Product/service.js";

export default {
  props: {
    name: String,
    brand: String,
    upc: String,
    sku: String,
    field: String,
    initUrl: String,
    productId: [Number, String],
    autoRepair: Boolean,
    autoImageDisabled: Boolean,
    productMetadata: Object,
  },
  data() {
    return {
      showDialog: false,
      imageUrl: null,
      imageResult: null,
      repairing: false,
      repairAttemptedFor: null,
      autoImageSuppressed: false,
      autoImageRequestId: 0,
    };
  },
  components: {
    MoreImage,
  },
  watch: {
    initUrl(val) {
      this.imageUrl = val;
      if (val !== this.repairAttemptedFor) this.repairAttemptedFor = null;
      this.$nextTick(this.maybeAutoPopulate);
    },
    name() {
      this.repairAttemptedFor = null;
      this.imageResult = null;
      this.$nextTick(this.maybeAutoPopulate);
    },
    autoImageDisabled: {
      immediate: true,
      handler(val) {
        this.autoImageSuppressed = Boolean(val);
      },
    },
    imageUrl(val) {
      if (val) this.$emit("submit", val);
    },
  },
  methods: {
    mediaMetadata(disabled) {
      return {
        ...(this.productMetadata || {}),
        autoImageDisabled: disabled,
      };
    },
    async findAndAttachFirstImage() {
      const requestId = ++this.autoImageRequestId;
      const identity = {
        q: this.name,
        brand: this.brand,
        upc: this.upc,
        sku: this.sku,
      };
      const searchResponse = await productService.searchProductImages(identity);
      const searchResult = searchResponse && searchResponse.data
        ? searchResponse.data
        : searchResponse;
      const candidate = searchResult && searchResult.candidates
        ? searchResult.candidates[0]
        : null;
      if (!candidate) throw new Error("No replacement image found");

      this.imageResult = searchResult;
      const importResponse = await productService.importProductImage({
        identity: {
          name: this.name,
          brand: this.brand,
          upc: this.upc,
          sku: this.sku,
        },
        sourceUrl: candidate.url,
        sourcePage: candidate.sourcePage || null,
        visibility: "shared_pending",
      });
      const imported = importResponse && importResponse.data
        ? importResponse.data
        : importResponse;
      if (!imported || !imported.imageUrl) throw new Error("Image import failed");
      if (this.autoImageSuppressed || requestId !== this.autoImageRequestId) return;

      if (this.productId) {
        await productService.update(this.productId, {
          imageUrl: imported.imageUrl,
          thumbnailUrl: imported.thumbnailUrl || imported.imageUrl,
          metadata: this.mediaMetadata(false),
        });
      }
      this.autoImageSuppressed = false;
      this.$emit("uploaded", {
        imageUrl: imported.imageUrl,
        thumbnailUrl: imported.thumbnailUrl || imported.imageUrl,
      });
      this.imageUrl = imported.imageUrl;
    },
    async maybeAutoPopulate() {
      if (
        !this.autoRepair ||
        !this.name ||
        this.imageUrl ||
        this.repairing ||
        this.autoImageSuppressed
      ) return;

      this.repairing = true;
      try {
        await this.findAndAttachFirstImage();
      } catch (error) {
        // A missing search result is not a form error. The user can still
        // upload or search manually from the image picker.
      } finally {
        this.repairing = false;
      }
    },
    async repairBrokenImage() {
      const failedUrl = this.imageUrl;
      if (
        !this.autoRepair ||
        !this.name ||
        !failedUrl ||
        this.repairing ||
        this.repairAttemptedFor === failedUrl
      ) return;

      this.repairAttemptedFor = failedUrl;
      this.repairing = true;
      try {
        await this.findAndAttachFirstImage();
      } catch (error) {
        this.imageUrl = null;
        this.$emit("submit", null);
      } finally {
        this.repairing = false;
      }
    },
    openMore() {
      this.showDialog = true;
    },
    async imageUploaded(val) {
      this.autoImageSuppressed = true;
      this.autoImageRequestId += 1;
      if (val && val.imageUrl) {
        this.imageUrl = val.imageUrl;
      }

      this.showDialog = false;
      this.$emit("auto-image-disabled", true);
      this.$emit("uploaded", val);
      if (this.productId && val && val.imageUrl) {
        await productService.update(this.productId, {
          imageUrl: val.imageUrl,
          thumbnailUrl: val.thumbnailUrl || val.imageUrl,
          metadata: this.mediaMetadata(true),
        });
      }
    },
    async imageRemove() {
      this.autoImageSuppressed = true;
      this.imageUrl = null;
      this.imageResult = null;
      this.$emit("submit", this.imageUrl);
      this.$emit("auto-image-disabled", true);
      if (this.productId) {
        await productService.update(this.productId, {
          imageUrl: null,
          thumbnailUrl: null,
          metadata: this.mediaMetadata(true),
        });
      }
    },
    async imageChange(val) {
      this.autoImageSuppressed = false;
      this.imageUrl = val;
      this.showDialog = false;
      this.$emit("auto-image-disabled", false);
      if (this.productId) {
        await productService.update(this.productId, {
          imageUrl: val,
          metadata: this.mediaMetadata(false),
        });
      }
    },
  },
  mounted() {
    this.imageUrl = this.initUrl || null;
    this.maybeAutoPopulate();
  },
};
</script>

<style scoped>
.image-card {
  max-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.image-box {
  max-height: 190px;
  object-fit: contain;
}

.image-repairing {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #71829d;
  font-size: 13px;
}
</style>
